/* RunIt wishlist — UI.
 *
 * The markup contract lives in snippets/runit-wishlist.liquid (the overlay) and
 * snippets/cart-drawer.liquid (the drawer tab + panel):
 *
 *   [data-runit-wishlist-root]            config: money format, sign-in state, routes
 *   [data-runit-wishlist-product]         a product the heart can save (its <button> is the toggle)
 *   [data-runit-wishlist-toggle]          the heart button itself
 *   [data-runit-wishlist-tab]             switch the drawer to the wishlist list
 *   [data-runit-bag-tab]                  switch back
 *   [data-runit-wishlist-panel]           the list inside the cart drawer
 *   [data-runit-wishlist-remove]          remove a saved product
 *   [data-runit-wishlist-add]             add the saved variant to the bag
 *   [data-runit-wishlist-options]         the per-row option picker (one compact
 *                                         dropdown per real Shopify option)
 *   [data-runit-wishlist-option-select]   pick a value of one option on a saved product
 *   [data-runit-wishlist-toast]           confirmation strip
 *   [data-runit-wishlist-sheet]           one-off "keep it across devices" prompt
 *
 * All clicks are delegated from document, so the hearts keep working after a
 * collection re-renders itself through the Section Rendering API.
 *
 * The bag and the wishlist stay independent: adding never unsaves, and the only
 * thing that removes an item is the shopper pressing the remove control.
 */
(function () {
  'use strict';

  var core = window.RunItWishlistCore;
  var config = document.querySelector('[data-runit-wishlist-root]');
  if (!core || !config || window.__rlWishlistBound) return;

  var storage;
  try {
    storage = window.localStorage;
  } catch (e) {
    storage = null;
  }
  /* Private-mode Safari throws on setItem; without storage the whole feature is
   * meaningless, so bow out before drawing hearts that cannot remember. */
  if (!storage) return;

  window.__rlWishlistBound = true;

  /* ------------------------------------------------------------ account sync
   *
   * A signed-in shopper's list lives on their Shopify customer account, so the UI
   * is pointed at a per-customer cache of it instead of the shared guest key, and
   * every change is mirrored to the account through the store's app proxy. Both
   * halves of that live in assets/runit-wishlist-account.js; nothing here changes
   * shape, only where the list is kept.
   *
   * The id in the markup is used for ONE thing — naming this shopper's cache and
   * queue so two accounts on a shared device cannot read each other. It is never
   * sent to the server, which learns who is signed in from the proxy signature. */
  var accountSyncUrl = config.getAttribute('data-sync-url') || '';
  var customerId = config.getAttribute('data-customer-id') || '';
  var accountKnown = config.getAttribute('data-account-known') === '1';

  function parseAccountItems(raw) {
    if (!raw) return null;
    var text = String(raw).trim();
    if (!text) return null;
    try {
      var parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /* null (not []) means "we do not know yet": Liquid only renders this when the
   * customer actually has the metafield, and an unknown account list is never
   * merged into — that is what keeps one shopper's items off another's account. */
  var accountItems = accountKnown ? parseAccountItems(config.getAttribute('data-account-items')) || [] : null;

  /* The account's deletions, rendered beside the list. Without them the first
   * paint of a device holding a stale copy would draw an item the shopper
   * removed elsewhere — and then upload it again. The endpoint sends them too;
   * this is so the very first frame is already right. */
  function parseAccountRemovals(raw) {
    if (!raw) return [];
    try {
      return core.sanitiseTombstones(JSON.parse(String(raw)));
    } catch (e) {
      return [];
    }
  }

  var accountRemovals = parseAccountRemovals(config.getAttribute('data-account-removed'));

  var account = window.RunItWishlistAccount && customerId
    ? window.RunItWishlistAccount.create({
        core: core,
        storage: storage,
        customerId: customerId,
        endpoint: accountSyncUrl,
        initialItems: accountItems,
        initialRemovals: accountRemovals
      })
    : null;

  var store = core.createStore(
    account ? core.scopedStorage(storage, core.cacheKey(customerId)) : storage
  );
  var moneyFormat = config.getAttribute('data-money-format') || '{{amount}}';
  var rootPath = config.getAttribute('data-root') || '/';
  var signedIn = config.getAttribute('data-customer') === 'true';
  var accountsEnabled = config.getAttribute('data-accounts') === 'true';
  var wishlistLabel = config.getAttribute('data-wishlist-label') || 'YOUR WISHLIST';
  var cartUrl = config.getAttribute('data-cart-url') || '/cart';
  var addUrl = config.getAttribute('data-cart-add-url') || '/cart/add';
  /* Back-in-stock alerts. The same Apps Script route the product page's Notify me
   * form posts to (snippets/runit-purchase-details.liquid), so a wishlist sign-up
   * lands on the same waitlist and gets the same restock email. Without a URL the
   * wishlist behaves exactly as before: sold-out values stay unpickable. */
  var restockUrl = (config.getAttribute('data-restock-url') || '').trim();
  var notifyOn = !!restockUrl && restockUrl.indexOf('REPLACE_WITH_') === -1;
  var customerEmail = (config.getAttribute('data-customer-email') || '').trim();
  var NOTIFIED_KEY = 'runit:wishlist-notified';
  var EMAIL_KEY = 'runit:restock-email';
  var refreshedHandles = {};
  var syncScheduled = false;

  /* Product payloads (/products/<handle>.js), shared by the price refresh and the
   * size picker so a page view never asks for the same product twice. */
  var products = {};
  var productRequests = {};
  var goneProducts = {};
  /* Variant ids in the bag right now, so a row can say IN CART. */
  var cartVariants = {};
  var cartRequest = null;
  var cartQueued = false;
  var cartTimer = null;
  /* Non-zero while we are writing to the DOM, so the observers below do not read
   * our own rendering back as "the cart changed". */
  var suppress = 0;

  /* Variants this browser has already asked to be told about, so the row can say
   * so instead of offering the form again. Per device on purpose: the waitlist
   * itself lives in the sheet, this only drives the label. */
  function notifiedMap() {
    try { return JSON.parse(storage.getItem(NOTIFIED_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function isNotified(variantId) {
    return !!(variantId && notifiedMap()[String(variantId)]);
  }
  function markNotified(variantId) {
    var map = notifiedMap();
    map[String(variantId)] = Date.now();
    try { storage.setItem(NOTIFIED_KEY, JSON.stringify(map)); } catch (e) {}
  }
  function rememberedEmail() {
    if (customerEmail) return customerEmail;
    try { return storage.getItem(EMAIL_KEY) || ''; } catch (e) { return ''; }
  }

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function escapeHtml(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function productUrl(handle) {
    return rootPath + 'products/' + handle;
  }

  /* Mirror one shopper-driven change to the account. Called only from the places
   * a person can change the list — never from a render — so a sync can never feed
   * itself. The local write has already happened by now, so this can fail freely.
   *
   * The two calls take different shapes and that asymmetry is the whole point:
   * `add` wants the whole entry (the server stores a product, a variant, a handle
   * and an addedAt), while `remove` wants the product ID and nothing else. Passing
   * the entry to `remove` is silently fatal rather than obviously wrong —
   * `idFromGid` stringifies anything it is handed, so the request goes out as
   * `{"productId":"[object Object]"}`, the Worker answers `changed:false`, and the
   * row vanishes from this device only to return on the next load or on any other
   * device. Signed out nothing is sent at all, which is why this could sit here
   * unnoticed until a real session exercised it. */
  function syncToAccount(kind, entry) {
    if (!account || account.mode !== 'account') return;
    if (kind === 'add') account.add(entry);
    else if (kind === 'remove' && entry && entry.id) account.remove(entry.id);
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function isWishlistView() {
    var el = drawer();
    return !!(el && el.classList.contains('rl-wishlist-open'));
  }

  /* Bag v2 (assets/runit-bag-v2.*) swaps the one-off sign-in sheet for the reference store's
   * snackbox wording; the account prompt stays as the footer line of the wishlist panel.
   * Off when PostHog `kill-aov-bag-v2` is on. */
  function bagV2() {
    return !document.documentElement.classList.contains('rl-off-bag-v2');
  }

  /* ------------------------------------------------------------------ toast */

  function hideToast() {
    var host = qs('[data-runit-wishlist-toast]');
    if (!host) return;
    host.classList.remove('is-visible');
    host.hidden = true;
  }

  function showToast(message, actionLabel, action) {
    var host = qs('[data-runit-wishlist-toast]');
    if (!host) return;
    var text = qs('[data-runit-wishlist-toast-text]', host);
    var button = qs('[data-runit-wishlist-toast-action]', host);
    if (text) text.textContent = message;
    if (button) {
      button.hidden = !actionLabel;
      button.textContent = actionLabel || '';
      button.__rlAction = action || null;
    }
    host.hidden = false;
    // Reflow so the entrance transition runs on a repeat toast.
    void host.offsetWidth;
    host.classList.add('is-visible');
    window.clearTimeout(host.__rlTimer);
    host.__rlTimer = window.setTimeout(hideToast, 5500);
  }

  /* --------------------------------------------------------------- sign-in prompt */

  function promptSeen() {
    try {
      return storage.getItem(core.PROMPT_SEEN_KEY) === '1';
    } catch (e) {
      return true;
    }
  }

  function markPromptSeen() {
    try {
      storage.setItem(core.PROMPT_SEEN_KEY, '1');
    } catch (e) {}
  }

  function hideSheet() {
    var sheet = qs('[data-runit-wishlist-sheet]');
    if (!sheet) return;
    sheet.classList.remove('is-visible');
    sheet.hidden = true;
  }

  /* Shown once per browser to a signed-out shopper — the item is already saved
   * on their device, so the sheet asks them to keep it, it does not gate the
   * save. Mirrors the reference store's "SAVE TO WISHLIST" prompt. */
  function maybePrompt(title) {
    var sheet = qs('[data-runit-wishlist-sheet]');
    if (!sheet || !accountsEnabled || signedIn || promptSeen()) return false;
    markPromptSeen();
    var slot = qs('[data-runit-wishlist-sheet-item]', sheet);
    if (slot) slot.textContent = title || '';
    sheet.hidden = false;
    void sheet.offsetWidth;
    sheet.classList.add('is-visible');
    return true;
  }

  /* ---------------------------------------------------------------- the list */

  /* shop.money_format on this store is `<span class=money>£{{amount}}</span>`, so
   * the formatted price is deliberately inserted as markup, not text — that is
   * what the storefront's own price elements use and what lets the currency
   * script see these amounts too. */
  var BAG_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="M4 6.5h12l-1 11H5l-1-11z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M7.2 6.5V5a2.8 2.8 0 0 1 5.6 0v1.5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
  var TICK_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  var BELL_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="M5 14V9a5 5 0 0 1 10 0v5l1.5 1.5h-13L5 14zM8.3 17.5a1.8 1.8 0 0 0 3.4 0" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';
  var TRASH_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="M3.5 5.5h13M8 5.5V3.5h4v2M5.5 5.5l.8 11h7.4l.8-11M8.5 8.5v5.5M11.5 8.5v5.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';

  function money(cents) {
    return core.formatMoney(cents, moneyFormat);
  }

  /* Product name → price → saved size (or size picker) → ADD TO CART → remove.
   * Everything below the price is filled in by paintRow(), which is the single
   * writer for the button, the size line and the chips, so a cart change or a
   * failed add can never leave two of them disagreeing. */
  function rowMarkup(item) {
    var url = productUrl(item.handle);
    /* Stock-state suffixes belong to the product page, not the saved list — the
       product cards drop it the same way (snippets/card-product.liquid). */
    var title = String(item.title || '').replace(/\s*—\s*Ready to Ship\s*$/i, '');
    var price = (item.hasRange ? 'From ' : '') + money(item.price);
    var compare = '';
    if (item.compare > item.price) {
      compare = '<s class="rl-wishlist-item__compare">' + money(item.compare) + '</s>';
    }
    var image = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="90" height="90">'
      : '';
    return '' +
      '<li class="rl-wishlist-item" data-runit-wishlist-item="' + escapeHtml(item.id) + '">' +
        '<a class="rl-wishlist-item__media" href="' + escapeHtml(url) + '" tabindex="-1" aria-hidden="true">' +
          image +
        '</a>' +
        '<div class="rl-wishlist-item__details">' +
          /* a personalised product's design ("DRIVE IT LIKE YOU STOLE IT"), saved from its page, sits in the
             title block so bag v2's card grid keeps it beside the photo */
          '<a class="rl-wishlist-item__title" href="' + escapeHtml(url) + '">' + escapeHtml(title) +
            (item.note ? '<span class="rl-wishlist-item__note">' + escapeHtml(item.note) + '</span>' : '') + '</a>' +
          '<p class="rl-wishlist-item__context" data-runit-wishlist-context hidden></p>' +
          '<p class="rl-wishlist-item__price">' + price + compare + '</p>' +
          '<p class="rl-wishlist-item__size" data-runit-wishlist-size hidden></p>' +
          '<span class="visually-hidden" data-runit-wishlist-status role="status" aria-live="polite"></span>' +
        '</div>' +
        '<button type="button" class="rl-wishlist-item__remove" data-runit-wishlist-remove="' + escapeHtml(item.id) + '" ' +
          'aria-label="Remove ' + escapeHtml(title) + ' from your wishlist">' +
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 14 14"><path d="M1 1l12 12M13 1L1 13" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
        '</button>' +
        /* Bag v2's card: a "⋯" menu in place of the ×. Hidden until paintRow makes
           the row a card, so the kill switch leaves today's row untouched. */
        '<div class="rl-wishlist-item__more" data-runit-wishlist-more hidden>' +
          '<button type="button" class="rl-wishlist-item__more-btn" data-runit-wishlist-more-toggle aria-haspopup="menu" aria-expanded="false" ' +
            'aria-label="More options for ' + escapeHtml(title) + '">' +
            '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 4"><circle cx="2" cy="2" r="1.8"/><circle cx="10" cy="2" r="1.8"/><circle cx="18" cy="2" r="1.8"/></svg>' +
          '</button>' +
          '<div class="rl-wishlist-item__menu" role="menu" data-runit-wishlist-menu hidden>' +
            '<button type="button" role="menuitem" class="rl-wishlist-item__menu-item" data-runit-wishlist-menu-add>' + BAG_ICON + 'Add to bag</button>' +
            '<button type="button" role="menuitem" class="rl-wishlist-item__menu-item" data-runit-wishlist-menu-notify hidden>' + BELL_ICON + 'Notify me</button>' +
            '<button type="button" role="menuitem" class="rl-wishlist-item__menu-item rl-wishlist-item__menu-item--remove" data-runit-wishlist-remove="' + escapeHtml(item.id) + '">' +
              TRASH_ICON + 'Remove from wishlist</button>' +
          '</div>' +
        '</div>' +
        /*
          The picker, the state lines and the button are SIBLINGS of the details
          column, not children of it, so the grid can give them the whole row on a
          phone (see the note on .rl-wishlist-item__options in runit-wishlist.css).
          Every one of them is still addressed by its data-* attribute, so nothing
          in the behaviour below knows they moved.
        */
        '<div class="rl-wishlist-item__options" data-runit-wishlist-options hidden></div>' +
        '<p class="rl-wishlist-item__stock" data-runit-wishlist-stock hidden></p>' +
        '<p class="rl-wishlist-item__error" data-runit-wishlist-error role="alert" hidden></p>' +
        '<button type="button" class="rl-wishlist-item__add" data-runit-wishlist-add>' +
          '<span class="rl-wishlist-item__add-label" data-runit-wishlist-add-label>ADD TO BAG</span>' +
          '<span class="rl-wishlist-item__add-icon" data-runit-wishlist-add-icon aria-hidden="true" hidden></span>' +
          '<span class="rl-wishlist-item__add-spinner" aria-hidden="true">' +
            '<svg class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>' +
          '</span>' +
        '</button>' +
        '<form class="rl-wishlist-item__notify" data-runit-wishlist-notify novalidate hidden>' +
          '<input type="email" name="email" autocomplete="email" placeholder="Email address" ' +
            'aria-label="Email address for a restock alert" required>' +
          '<button type="submit" class="rl-wishlist-item__notify-submit">NOTIFY ME</button>' +
          '<p class="rl-wishlist-item__notify-msg" data-runit-wishlist-notify-msg role="status" aria-live="polite"></p>' +
        '</form>' +
        '<button type="button" class="rl-wishlist-item__change" data-runit-wishlist-change hidden></button>' +
      '</li>';
  }

  function syncHearts(items) {
    var ids = {};
    for (var i = 0; i < items.length; i++) ids[items[i].id] = true;
    qsa('[data-runit-wishlist-product]').forEach(function (host) {
      var button = qs('[data-runit-wishlist-toggle]', host);
      if (!button) return;
      var saved = !!ids[host.getAttribute('data-product-id')];
      var label = qs('[data-runit-wishlist-sr]', button);
      // write only what changed: an unchanged write still triggers the body observer that calls us
      var next = core.heartUpdates({
        pressed: button.getAttribute('aria-pressed'),
        title: button.getAttribute('title'),
        label: label ? label.textContent : null
      }, saved);
      if (next.pressed) button.setAttribute('aria-pressed', next.pressed);
      if (next.title) button.setAttribute('title', next.title);
      if (next.label && label) label.textContent = next.label;
    });
  }

  function syncTabs(count) {
    qsa('[data-runit-wishlist-tab-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    var inline = qs('[data-runit-wishlist-tab-inline]');
    if (inline) {
      inline.textContent = count ? ' (' + count + ')' : '';
    }
  }

  function render(items) {
    var list = qs('[data-runit-wishlist-list]');
    var saved = items || store.all();
    var ticket = focusTicket();
    withSuppress(function () {
      if (list) {
        var markup = '';
        for (var i = 0; i < saved.length; i++) markup += rowMarkup(saved[i]);
        list.innerHTML = markup;
        list.hidden = saved.length === 0;
      }
      var empty = qs('[data-runit-wishlist-empty]');
      if (empty) empty.hidden = saved.length > 0;
      var signin = qs('[data-runit-wishlist-signin]');
      if (signin) signin.hidden = signedIn || saved.length === 0;
      paintRows();
    });
    restoreFocus(ticket);
    syncTabs(saved.length);
    syncHearts(saved);
  }

  /* ------------------------------------------------------------- the button */

  var NO_OPTIONS = { groups: [], autoSelected: [], selection: [], variant: null,
                     missing: [], complete: false, isSizeOnly: false, hasPrompt: false };

  /* Everything the button needs to know about one row, all of it derived from
   * the stored entry plus whatever product and cart data we have. */
  function rowState(entry) {
    var product = products[entry.handle] || null;
    var gone = goneProducts[entry.handle] === true;
    var options = product ? core.optionState(product, savedOptions(product, entry)) : NO_OPTIONS;
    /* Bag v2: always the compact card. It asks for the size, plus any other option
     * the save did not record (a heart on a collection card knows no colour). Those
     * extra dropdowns stay for the life of the row, so picking a colour never swaps
     * the card for a different layout that asks again (owner 2026-09-30). */
    var card = bagV2();
    var hasSize = options.groups.some(function (g) { return g.isSize; });
    /* On a card an unchosen save's variant is only the page's default, not a pick. */
    var bought = inCart(entry) && (!card || entry.variantChosen === true);
    var variantCount = product && Array.isArray(product.variants) ? product.variants.length : null;
    var anythingAvailable = product ? core.availableVariants(product).length : null;
    var others = alternatives(options, entry);
    /* Availability of what the row is pointing at: the shopper's own selection
     * once every option is named, otherwise the variant it was saved with. An
     * impossible combination is never available. */
    var available;
    if (gone) available = false;
    else if (options.complete) available = !!(options.variant && options.variant.available === true);
    else available = entry.available === true;
    /* Is the row waiting on the shopper to name a variant? Either an option is
     * still unpicked, or the combination they named is not a real variant. A
     * heart on a product card cannot know which variant the shopper wants, so a
     * save made there asks — and now that means every option the product really
     * has, not just a size. Single-variant products have nothing to ask. */
    var unresolved = options.complete && !options.variant;
    return {
      product: product,
      gone: gone,
      card: card,
      options: options,
      prompt: core.promptFor(options),
      inCart: bought,
      available: available,
      needsChoice: !bought && !gone && variantCount !== 1 &&
        (options.missing.length > 0 || unresolved ||
         /* a card with no size to ask for is already chosen by its saved context */
         (options.complete && entry.variantChosen !== true && !(card && !hasSize))),
      alternatives: others,
      /* Sold out in the saved variant, but the product still has other variants. */
      hasAlternatives: !gone && (others > 0 || !product),
      anythingAvailable: anythingAvailable
    };
  }

  /* Another buyable variant the row could move to — the state of every value,
   * read against the current selection, so an impossible combination is never
   * offered as a way out. */
  function alternatives(options, entry) {
    var count = 0;
    for (var g = 0; g < options.groups.length; g++) {
      var values = options.groups[g].values;
      for (var v = 0; v < values.length; v++) {
        if (values[v].available && values[v].variantId && values[v].variantId !== entry.variantId) count++;
      }
    }
    return count;
  }

  /* One compact dropdown per real option (Size, Print Type, … and anything added
   * to the catalogue later). Options with a single value are not drawn at all —
   * there is nothing to choose — but they are still part of the selection the
   * variant is resolved from, so a single Non-Reflective value never becomes a
   * pointless one-option dropdown.
   *
   * A native <select> is the control on purpose: it is the platform's own picker
   * on iOS and Android (so it cannot be clipped by the drawer, cannot overflow a
   * 320px viewport, and is keyboard- and VoiceOver-navigable for free), and a
   * value that cannot complete a real variant is a `disabled` <option> — shown,
   * greyed, unselectable — which is what makes an impossible combination
   * un-submittable rather than merely discouraged. */
  function optionsMarkup(options, card) {
    var out = '';
    for (var g = 0; g < options.groups.length; g++) {
      var group = options.groups[g];
      var picked = false;
      var choices = '';
      for (var v = 0; v < group.values.length; v++) {
        var value = group.values[v];
        if (value.selected) picked = true;
        var note = value.available ? '' : value.exists ? ' \u2014 sold out' : ' \u2014 unavailable';
        choices += '<option value="' + escapeHtml(value.value) + '"' +
          (value.selected ? ' selected' : '') +
          ((notifyOn ? !value.exists : value.disabled) ? ' disabled' : '') +
          '>' + escapeHtml(value.label + note) + '</option>';
      }
      if (!picked) {
        choices = '<option value="" disabled selected>' +
          escapeHtml(card ? group.name : 'Choose ' + group.name.toLowerCase()) + '</option>' + choices;
      }
      out += '<div class="rl-wishlist-item__option" data-runit-wishlist-option-group="' +
        group.index + '">' +
        '<span class="rl-wishlist-item__option-name" id="rl-wl-opt-' + group.index + '-label">' +
          escapeHtml(group.name) + '</span>' +
        '<select class="rl-wishlist-item__select" ' +
          'data-runit-wishlist-option-select="' + group.index + '" ' +
          (picked ? '' : 'data-runit-wishlist-option-empty="true" ') +
          'aria-labelledby="rl-wl-opt-' + group.index + '-label">' +
          choices +
        '</select>' +
        '</div>';
    }
    return out;
  }

  var BUTTON_LABELS = {
    busy: 'ADDING\u2026',
    'in-cart': '\u2713 IN CART',
    gone: 'NO LONGER AVAILABLE',
    'sold-out': 'SOLD OUT',
    notify: 'NOTIFY ME WHEN BACK',
    notified: '\u2713 WE\u2019LL EMAIL YOU',
    add: 'ADD TO BAG'
  };

  function paintRow(row) {
    var entry = store.get(row.getAttribute('data-runit-wishlist-item'));
    if (!entry) return;
    var state = rowState(entry);
    var addBtn = qs('[data-runit-wishlist-add]', row);
    var addLabel = qs('[data-runit-wishlist-add-label]', row);
    var sizeLine = qs('[data-runit-wishlist-size]', row);
    var sizes = qs('[data-runit-wishlist-options]', row);
    var stock = qs('[data-runit-wishlist-stock]', row);
    var change = qs('[data-runit-wishlist-change]', row);
    var card = state.card;
    row.classList.toggle('rl-wishlist-item--card', card);

    /* · bag v2 card: the colour/print it was saved in, one line each */
    var contextLine = qs('[data-runit-wishlist-context]', row);
    if (contextLine) {
      /* every option is a dropdown on the card now, so there is no separate context line */
      var seen = [];
      contextLine.hidden = !seen.length;
      contextLine.innerHTML = seen.map(function (v) { return '<span>' + escapeHtml(v) + '</span>'; }).join('');
    }

    /* · bag v2 card: the photo follows the colour/print picked so far — the first
     *   variant matching every chosen option (unchosen ones match anything) that
     *   has its own photo. */
    var mediaImg = card && state.product ? qs('.rl-wishlist-item__media img', row) : null;
    if (mediaImg && Array.isArray(state.product.variants)) {
      var sel = state.options.selection || [];
      var hit = state.product.variants.find(function (pv) {
        return pv.featured_image && (pv.options || []).every(function (o, k) { return !sel[k] || sel[k] === o; });
      });
      var src = hit && hit.featured_image && hit.featured_image.src;
      if (src) {
        src = String(src).replace(/^\/\//, 'https://').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
        src += (src.indexOf('?') < 0 ? '?' : '&') + 'width=240';
        if (mediaImg.getAttribute('src') !== src) mediaImg.setAttribute('src', src);
      }
    }

    /* · bag v2 card: the price of the colour/print it was saved in (a print can
     *   cost more than the product's "from" price the heart carried) */
    var priceLine = qs('.rl-wishlist-item__price', row);
    if (card && priceLine && state.options.variant) {
      var v = state.options.variant;
      var priced = money(v.price) + (Number(v.compare_at_price) > Number(v.price)
        ? '<s class="rl-wishlist-item__compare">' + money(v.compare_at_price) + '</s>' : '');
      if (priceLine.innerHTML !== priced) priceLine.innerHTML = priced;
    }

    /* · the saved size ("Size: XL · Reflective"), or hidden while a size has
     *   still to be chosen */
    if (sizeLine) {
      var label = core.variantLabel(state.product, entry.variantTitle);
      var showSize = !!label.size && !state.needsChoice && !card;
      sizeLine.hidden = !showSize;
      sizeLine.innerHTML = showSize
        ? (label.isSize ? '<span class="rl-wishlist-item__size-key">Size:</span> ' : '') +
          escapeHtml(label.size) +
          (label.extra ? ' <span class="rl-wishlist-item__size-extra">\u00b7 ' + escapeHtml(label.extra) + '</span>' : '')
        : '';
    }

    /* · the option picker — one row per option the product really has. Rebuilt
     *   only when the pills themselves change (selection, availability), so the
     *   shopper's open picker is never yanked shut underneath them. */
    if (sizes) {
      var opts = state.options;
      /* the card shows every real option, pre-filled with the colour/print it was saved in, so
       * every card looks the same and any of them can be changed (owner 2026-09-30). The size is
       * never pre-filled. */
      var signature = card ? 'card|' : '';
      for (var g = 0; g < opts.groups.length; g++) {
        for (var v = 0; v < opts.groups[g].values.length; v++) {
          var value = opts.groups[g].values[v];
          signature += opts.groups[g].index + ':' + value.value +
            (value.selected ? '=' : (notifyOn ? !value.exists : value.disabled) ? '-' : '+') + ',';
        }
      }
      if (sizes.getAttribute('data-signature') !== signature) {
        sizes.setAttribute('data-signature', signature);
        sizes.innerHTML = optionsMarkup(opts, card);
      }
      /* The picker is part of the row, not something to open: two options sit on
       * one line under the price, one option takes the full width. It never
       * collapses, so the shopper can always change their mind after adding. */
      sizes.hidden = !opts.groups.length;
    }

    /* · why the button cannot add anything. While the shopper is still choosing,
     *   the picker itself says what is unavailable, so the row stays quiet. */
    if (stock) {
      var stockText = '';
      if (state.gone) stockText = 'No longer available';
      else if (state.needsChoice) stockText = '';
      /* `anythingAvailable` is a count: another variant to offer, or none. */
      else if (!state.available && notifyOn && isNotified(notifyVariantId(state, entry))) {
        stockText = 'Sold out \u2014 you\u2019re on the restock list';
      }
      else if (!state.available) {
        if (state.anythingAvailable > 0) {
          stockText = state.options.isSizeOnly ? 'Sold out in your size' : 'Sold out in your options';
        } else {
          stockText = 'Sold out';
        }
      }
      stock.textContent = stockText;
      stock.hidden = !stockText;
    }

    /* · the button itself */
    if (addBtn) {
      var mode;
      if (addBtn.__rlBusy) mode = 'busy';
      else if (state.inCart) mode = 'in-cart';
      else if (state.gone) mode = 'gone';
      /* Asking comes before SOLD OUT: a product saved from a card may well have a
       * sold-out default variant, and "CHOOSE A SIZE" is the useful thing to say
       * until the shopper has actually named a variant. */
      else if (state.needsChoice) mode = 'choose';
      else if (!state.available) {
        var wantedId = notifyOn ? notifyVariantId(state, entry) : '';
        mode = wantedId ? (isNotified(wantedId) ? 'notified' : 'notify') : 'sold-out';
      }
      else mode = 'add';

      /* "CHOOSE A SIZE" / "CHOOSE PRINT TYPE" / "CHOOSE OPTIONS" — named from the
       * options the product actually has, never from an assumed one. */
      if (addLabel) addLabel.textContent = mode === 'choose'
        ? (state.prompt || 'CHOOSE OPTIONS')
        : BUTTON_LABELS[mode];
      addBtn.setAttribute('data-runit-wishlist-add-state', mode);
      addBtn.classList.toggle('is-in-cart', mode === 'in-cart');
      addBtn.classList.toggle('is-loading', mode === 'busy');
      addBtn.classList.toggle('is-muted', mode === 'sold-out' || mode === 'gone' || mode === 'notified');
      addBtn.classList.toggle('is-notify', mode === 'notify');
      /* a card's bag button stays pressable with no size: it says "Please select a size" */
      addBtn.disabled = mode === 'busy' || mode === 'sold-out' || mode === 'gone' || (mode === 'choose' && !card) || mode === 'notified';
      if (mode !== 'notify') row.__rlNotifyOpen = false;
      var notifyForm = qs('[data-runit-wishlist-notify]', row);
      if (notifyForm) {
        var open = mode === 'notify' && row.__rlNotifyOpen === true;
        notifyForm.hidden = !open;
        addBtn.hidden = open;
      }
      addBtn.removeAttribute('aria-expanded');
      /* The label changes are the whole feedback, so name the action for screen
       * readers too — "✓ IN CART" on its own does not say what pressing does. */
      if (mode === 'in-cart') addBtn.setAttribute('title', 'View your bag');
      else addBtn.removeAttribute('title');

      /* · bag v2 card: a square bag button (tick once in the bag); a sold-out
       *   size is offered "Notify me" from the ⋯ menu instead */
      var icon = qs('[data-runit-wishlist-add-icon]', row);
      var buyable = mode === 'add' || mode === 'choose' || mode === 'busy' || mode === 'in-cart';
      if (icon) {
        icon.hidden = !card;
        var glyph = card ? (mode === 'in-cart' ? TICK_ICON : BAG_ICON) : '';
        if (icon.innerHTML !== glyph) icon.innerHTML = glyph;
      }
      if (card) {
        if (!buyable) addBtn.hidden = true;
        addBtn.setAttribute('aria-label', mode === 'in-cart' ? 'In your bag — view bag' : 'Add to bag');
      } else {
        addBtn.removeAttribute('aria-label');
      }
      var more = qs('[data-runit-wishlist-more]', row);
      if (more) {
        more.hidden = !card;
        var menuAdd = qs('[data-runit-wishlist-menu-add]', more);
        var menuNotify = qs('[data-runit-wishlist-menu-notify]', more);
        if (menuAdd) menuAdd.hidden = !buyable;
        if (menuNotify) menuNotify.hidden = mode !== 'notify';
      }
    }

    /* · the way to another size */
    if (change) {
      /* Offered while the row is in the bag too: the shopper may want a second
       * size, and hiding it here would collapse the row's height the moment the
       * item was added. */
      var showChange = !card && !state.gone && !state.needsChoice &&
        (state.alternatives > 0 || (!state.available && state.hasAlternatives));
      change.hidden = !showChange;
      if (showChange) {
        change.textContent = state.options.isSizeOnly
          ? (state.available ? 'Change size' : 'Choose another size')
          : (state.available ? 'Change options' : 'Choose another option');
        change.removeAttribute('aria-expanded');
      }
    }
  }

  function paintRows() {
    var rows = qsa('[data-runit-wishlist-item]');
    if (!rows.length) return;
    withSuppress(function () {
      for (var i = 0; i < rows.length; i++) paintRow(rows[i]);
    });
  }

  /* The picker is always on screen, so "opening" it now means putting the
   * shopper's cursor in it — what "Change size" has to do once there is no
   * collapsed state left to expand. */
  function setOptionsOpen(row, show, focus) {
    var sizes = qs('[data-runit-wishlist-options]', row);
    if (!sizes) return;
    var addBtn = qs('[data-runit-wishlist-add]', row);
    if (addBtn) addBtn.removeAttribute('aria-expanded');
    var change = qs('[data-runit-wishlist-change]', row);
    if (change) change.removeAttribute('aria-expanded');
    if (show && focus) {
      clearRowError(row);
      var first = sizes.querySelector('[data-runit-wishlist-option-select]');
      if (first && first.focus) first.focus({ preventScroll: true });
    }
  }

  /* A row is rebuilt wholesale whenever the store changes (render → rowMarkup),
   * which throws away the element the shopper is standing on. Remembering which
   * dropdown had focus — and which row it belonged to — lets paintRow put the
   * cursor back where it was instead of dropping it on <body>. */
  function focusTicket() {
    var el = document.activeElement;
    if (!el || !el.matches || !el.matches('[data-runit-wishlist-option-select]')) return null;
    var row = el.closest('[data-runit-wishlist-item]');
    if (!row) return null;
    return { id: row.getAttribute('data-runit-wishlist-item'),
             index: el.getAttribute('data-runit-wishlist-option-select') };
  }

  /* Not on touch screens: focusing a <select> in code re-opens its picker on iOS, so
   * every pick (Reflective, then the size) popped the picker up a second time. The
   * rebuilt row keeps the pick either way; only keyboard users need the cursor back. */
  var TOUCH = window.matchMedia ? window.matchMedia('(pointer: coarse)') : { matches: false };
  function restoreFocus(ticket) {
    if (!ticket || TOUCH.matches) return;
    var row = rowFor(ticket.id);
    if (!row) return;
    var el = qs('[data-runit-wishlist-option-select="' + ticket.index + '"]', row);
    if (el && el.focus) el.focus({ preventScroll: true });
  }

  /* Put the cursor in the dropdown the shopper still has to fill in. The product
   * payload may still be in flight — a row only knows its options once it has it
   * — so a press before that waits for it and then focuses. */
  function revealOptions(row, entry) {
    var sizes = qs('[data-runit-wishlist-options]', row);
    if (!sizes) return;
    if (!sizes.children.length) {
      setRowStatus(row, 'Loading options\u2026');
      loadProduct(entry.handle).then(function () {
        var fresh = rowFor(entry.id);
        if (!fresh) return;
        paintRow(fresh);
        revealOptions(fresh, store.get(entry.id) || entry);
      });
      return;
    }
    var unset = qs('[data-runit-wishlist-option-empty="true"]', sizes) ||
      qs('[data-runit-wishlist-option-select]', sizes);
    if (unset && unset.focus) unset.focus({ preventScroll: true });
    setOptionsOpen(row, true, false);
  }

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;
    window.requestAnimationFrame(function () {
      syncScheduled = false;
      syncHearts(store.all());
    });
  }

  /* ------------------------------------------------- product + bag state */

  function withSuppress(fn) {
    suppress++;
    try {
      fn();
    } finally {
      window.setTimeout(function () {
        suppress--;
      }, 0);
    }
  }

  /* One request per product per page view. A product that has been deleted from
   * the catalogue answers 404 and is remembered as gone, so the row can say so
   * instead of offering a bag it can never deliver. */
  function loadProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (products[handle]) return Promise.resolve(products[handle]);
    if (goneProducts[handle] || productRequests[handle]) {
      return productRequests[handle] || Promise.resolve(null);
    }
    productRequests[handle] = fetch(rootPath + 'products/' + handle + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) {
          if (res.status === 404 || res.status === 410) {
            goneProducts[handle] = true;
            /* This retired clearance tee has a verified current listing. Keep
               other deleted products marked gone rather than guessing a match. */
            if (handle === 'what-the-fck-is-a-speedlimit-tee-last-sizes') {
              return loadProduct('what-the-fck-is-a-speedlimit-tee').then(function (current) {
                if (!current) {
                  if (!goneProducts['what-the-fck-is-a-speedlimit-tee']) delete goneProducts[handle];
                  throw new Error('Replacement lookup failed');
                }
                if (String(current.id) !== '9840064463197') throw new Error('Retired listing');
                delete goneProducts[handle];
                return current;
              });
            }
          }
          throw new Error('HTTP ' + res.status);
        }
        return res.json();
      })
      .then(function (product) {
        products[handle] = product;
        return product;
      })
      .catch(function () {
        /* A network error or temporary server failure does not remove a product. */
        return null;
      })
      .then(function (product) {
        delete productRequests[handle];
        return product;
      });
    return productRequests[handle];
  }

  /* Keep price and stock honest. Runs when the wishlist is opened, once per
   * handle per page view, and never blocks the list from painting. */
  function refreshFromStorefront() {
    var items = store.all();
    var pending = items.filter(function (item) {
      return !refreshedHandles[item.handle];
    });
    if (!pending.length) return;
    var next = items.slice();
    return Promise.all(pending.map(function (item) {
      refreshedHandles[item.handle] = true;
      return loadProduct(item.handle).then(function (product) {
        if (!product) {
          if (!goneProducts[item.handle]) delete refreshedHandles[item.handle];
          return;
        }
        for (var i = 0; i < next.length; i++) {
          if (next[i].id === item.id) {
            next[i] = core.applyProductData(next[i], product);
            if (product.handle && product.handle !== item.handle) {
              next[i].id = String(product.id);
              next[i].handle = product.handle;
            }
            return;
          }
        }
      });
    })).then(function () {
      store.replace(next);
    });
  }

  function rowFor(id) {
    var rows = qsa('[data-runit-wishlist-item]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-runit-wishlist-item') === String(id)) return rows[i];
    }
    return null;
  }

  function variantFor(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(variantId)) return product.variants[i];
    }
    return null;
  }

  /* What this row is holding: the values the shopper picked here, or — for an
   * entry saved from the bag, whose variant they did buy — that variant's own
   * values. A heart on a product card picks nothing, and nothing is never filled
   * in from the default variant: guessing a size is exactly what this must not
   * do. Entries stored before selections existed fall back to their variant. */
  function savedOptions(product, entry) {
    return withContext(product, entry, storedOptions(product, entry));
  }

  function storedOptions(product, entry) {
    var stored = Array.isArray(entry.options) ? entry.options : [];
    for (var i = 0; i < stored.length; i++) {
      if (stored[i]) return stored.slice();
    }
    if (entry.variantChosen) {
      var variant = variantFor(product, entry.variantId);
      if (variant && Array.isArray(variant.options)) return variant.options.slice();
    }
    return [];
  }

  /* Bag v2 only: blanks other than the size are filled from the colour/print the
   * item was saved in (entry.context). The size is never filled — that is the one
   * thing the card asks for. With the kill switch on, nothing changes. */
  function withContext(product, entry, selection) {
    var context = Array.isArray(entry.context) ? entry.context : [];
    if (!bagV2() || !context.length) return selection;
    var sizeAt = core.sizeOptionIndex(product);
    var out = selection.slice();
    for (var i = 0; i < context.length; i++) {
      if (i !== sizeAt && context[i] && !out[i]) out[i] = context[i];
    }
    return out;
  }

  function inCart(entry) {
    return !!entry && !!entry.variantId && cartVariants[String(entry.variantId)] === true;
  }

  /* Read the bag once and repaint the rows from it. Single-flight: the theme
   * swaps both #CartDrawer and the icon bubble on every cart change, and a
   * burst of those signals collapses into one request. */
  function syncCartState(delay) {
    window.clearTimeout(cartTimer);
    cartTimer = window.setTimeout(function () {
      if (cartRequest) {
        cartQueued = true;
        return;
      }
      cartRequest = fetch(rootPath + 'cart.js', {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items)) return;
          var next = {};
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].quantity > 0) next[String(cart.items[i].variant_id)] = true;
          }
          cartVariants = next;
        })
        .catch(function () {})
        .then(function () {
          cartRequest = null;
          paintRows();
          if (cartQueued) {
            cartQueued = false;
            syncCartState(0);
          }
        });
    }, delay === undefined ? 150 : delay);
  }

  /* ------------------------------------------------------- bag ⇄ bag view */

  /* "✓ IN CART" sends the shopper to the bag. The theme's drawer is the bag; if
   * some other theme ever renders this snippet without one, fall back to /cart
   * rather than doing nothing. */
  function viewBag() {
    var el = drawer();
    if (!el) {
      window.location.href = cartUrl;
      return;
    }
    showBag();
    if (!el.classList.contains('active')) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.click();
    }
  }

  /* The theme re-renders the whole cart drawer after a cart change, which throws
   * our panel away with it. This swaps that fresh markup in the same way the
   * theme's own cart JS does, then puts the wishlist view back on top of it. */
  function applySections(sections) {
    if (!sections) return;
    var el = drawer();
    var wasOpen = el ? el.classList.contains('active') : false;
    var wasWishlist = el ? el.classList.contains('rl-wishlist-open') : false;

    var fresh = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
    var freshInner = fresh.querySelector('#CartDrawer');
    var liveInner = document.getElementById('CartDrawer');
    if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;
    var freshDrawer = fresh.querySelector('cart-drawer');
    if (el && freshDrawer) {
      el.className = freshDrawer.className;
      if (wasOpen) el.classList.add('active', 'animate');
      if (wasWishlist) el.classList.add('rl-wishlist-open');
    }

    /* The cart-icon-bubble *section* renders the icon and the count bubble, not
     * the header's #cart-icon-bubble wrapper (that id is added by the header
     * section), so take the whole rendered section when the id is absent. */
    var bubbleFresh = sections['cart-icon-bubble'];
    var liveBubble = document.getElementById('cart-icon-bubble');
    if (liveBubble && bubbleFresh) {
      var bubbleDoc = new DOMParser().parseFromString(bubbleFresh, 'text/html');
      var bubble = bubbleDoc.querySelector('#cart-icon-bubble');
      liveBubble.innerHTML = bubble ? bubble.innerHTML : bubbleDoc.body.innerHTML;
    }

    setHeading(wasWishlist);
    render();
  }

  /* --------------------------------------------------------- add to cart */

  function messageFor(error) {
    var reason = error && error.message ? String(error.message) : '';
    if (/HTTP 422|HTTP 4\d\d/.test(reason)) return 'That size just sold out. Pick another one.';
    if (/HTTP 5\d\d/.test(reason)) return 'Something went wrong on our end. Try again in a moment.';
    if (!window.navigator.onLine) return 'You appear to be offline. Try again once you are back.';
    return 'We could not add that to your bag. Try again.';
  }

  function setRowError(row, message) {
    if (!row) return;
    var el = qs('[data-runit-wishlist-error]', row);
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
    setRowStatus(row, message || '');
  }

  function clearRowError(row) {
    setRowError(row, '');
  }

  function setRowStatus(row, message) {
    if (!row) return;
    var el = qs('[data-runit-wishlist-status]', row);
    if (el) el.textContent = message || '';
  }

  /* Record one option value on the row, then resolve the variant it names.
   *
   * Half a selection is saved as half a selection (no variant is claimed) so the
   * shopper's picks survive a refresh; a complete one is saved as its exact
   * variant and added — unless that variant is already in the bag, which would
   * only put a second identical line in it. */
  /* The exact variant a restock alert is for: the shopper's full selection, or
   * the only variant a single-variant product has. Never a guess. */
  function notifyVariantId(state, entry) {
    if (!state || state.gone || state.needsChoice) return '';
    if (state.options.complete && state.options.variant) return String(state.options.variant.id);
    var product = state.product;
    if (product && Array.isArray(product.variants) && product.variants.length === 1) {
      return String(product.variants[0].id);
    }
    return '';
  }

  /* Same shape as the product page's restockPayload() in runit-tweaks.js, so the
   * Apps Script stores a wishlist sign-up exactly like a product-page one. */
  function restockPayload(product, variant, email) {
    var names = Array.isArray(product.options) ? product.options : [];
    var optionNames = {};
    var size = '', printType = '', colour = '';
    (variant.options || []).forEach(function (value, index) {
      var name = String((names[index] && (names[index].name || names[index])) || ('Option ' + (index + 1)));
      value = String(value || '');
      optionNames[name] = value;
      if (/size/i.test(name)) size = value;
      else if (/print.*(type|finish)|finish/i.test(name) || /reflect/i.test(value)) printType = value;
      else if (/colou?r/i.test(name)) colour = value;
    });
    var lower = ((product.type || product.product_type || '') + ' ' + (product.title || '')).toLowerCase();
    var category = /cargo|trouser|pant/.test(lower) ? 'armoured-cargo'
      : /armou?red|armor/.test(lower) ? 'armoured-hoodie'
      : (/t-?shirt|\btee\b/.test(lower) && lower.indexOf('hoodie') < 0) ? 't-shirt'
      : /hoodie/.test(lower) ? 'hoodie' : '';
    var image = product.featured_image || (product.images && product.images[0]) || '';
    if (image && image.indexOf('//') === 0) image = location.protocol + image;
    var values = (variant.options || []).filter(function (v) { return String(v || '').trim(); });
    return {
      email: email,
      variantId: String(variant.id),
      variantTitle: values.length ? values.join(' / ') : String(variant.title || ''),
      productId: String(product.id || ''),
      productHandle: product.handle || '',
      productTitle: product.title || '',
      productUrl: location.origin + productUrl(product.handle),
      productImage: image,
      category: category,
      size: size,
      printType: printType,
      colour: colour,
      options: optionNames
    };
  }

  function openNotify(row, entry) {
    row.__rlNotifyOpen = true;
    paintRow(row);
    var input = qs('[data-runit-wishlist-notify] input[name="email"]', row);
    if (input) {
      if (!input.value) input.value = rememberedEmail();
      input.focus();
    }
  }

  function submitNotify(form) {
    var row = form.closest('[data-runit-wishlist-item]');
    var entry = row ? store.get(row.getAttribute('data-runit-wishlist-item')) : null;
    var msg = qs('[data-runit-wishlist-notify-msg]', form);
    var input = form.querySelector('input[name="email"]');
    var button = form.querySelector('button[type="submit"]');
    var say = function (text) { if (msg) msg.textContent = text; };
    if (!entry || form.__rlBusy) return;
    var email = input ? input.value.trim() : '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { say('Enter a valid email address.'); return; }
    var state = rowState(entry);
    var variantId = notifyVariantId(state, entry);
    var variant = null;
    (state.product && state.product.variants || []).forEach(function (v) { if (String(v.id) === variantId) variant = v; });
    if (!variant) { say('Choose your options first.'); return; }
    if (variant.available) { row.__rlNotifyOpen = false; paintRow(row); return; }
    form.__rlBusy = true;
    if (button) button.disabled = true;
    say('Saving\u2026');
    /* No Content-Type header: it must stay a CORS simple request, or the browser
     * preflights and Apps Script never sees it (tests/runit-restock-notify.test.js). */
    fetch(restockUrl + (restockUrl.indexOf('?') >= 0 ? '&' : '?') + 'action=storefrontSubscribe', {
      method: 'POST',
      body: JSON.stringify(restockPayload(state.product, variant, email))
    })
      .then(function (res) { return res.json(); })
      .then(function (result) {
        if (result && result.ok) {
          markNotified(variantId);
          if (!customerEmail) { try { storage.setItem(EMAIL_KEY, email); } catch (e) {} }
          row.__rlNotifyOpen = false;
          say('');
          paintRow(row);
        } else {
          say((result && result.message) || 'We couldn\u2019t save that just now. Please try again.');
        }
      })
      .catch(function () { say('We couldn\u2019t save that just now. Please try again.'); })
      .then(function () {
        form.__rlBusy = false;
        if (button) button.disabled = false;
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-runit-wishlist-notify]')) return;
    event.preventDefault();
    submitNotify(form);
  });

  function chooseOption(row, entry, index, value) {
    var product = products[entry.handle] || null;
    if (!product) {
      loadProduct(entry.handle).then(function () {
        var fresh = rowFor(entry.id);
        if (fresh) chooseOption(fresh, store.get(entry.id) || entry, index, value);
      });
      return;
    }
    var selection = savedOptions(product, entry).slice();
    selection[index] = value;
    commitSelection(entry, product, selection);
    paintRows();
  }

  /* Record a selection on the saved item (and the variant it names, once it names
   * one) and mirror it to the account. Returns the stored entry. */
  function commitSelection(entry, product, selection) {
    var options = core.optionState(product, selection);
    var chosen = core.sanitiseEntry(entry);
    chosen.options = options.selection;
    if (options.complete && options.variant) {
      var variant = options.variant;
      chosen.variantId = String(variant.id);
      chosen.variantChosen = true;
      chosen.variantTitle = String(variant.title || '');
      chosen.available = variant.available === true;
      chosen.price = Number(variant.price) || 0;
      chosen.hasRange = false;
      chosen.compare = Number(variant.compare_at_price) || 0;
    } else {
      /* Still choosing: keep the picks, claim nothing about a variant. */
      chosen.variantId = '';
      chosen.variantChosen = false;
      chosen.variantTitle = '';
    }
    chosen.addedAt = entry.addedAt || Date.now();
    /* Choosing is not buying. The pick is recorded on the saved item — so it
     * survives a refresh — and the row's own button turns into ADD TO CART; that
     * button is what puts the exact variant in the bag. Changing a dropdown on an
     * item already in the bag therefore never silently adds a second line. */
    store.add(chosen);
    /* The chosen variant is what the account must remember, not the default
     * variant the heart happened to carry. */
    syncToAccount('add', chosen);
    return store.get(chosen.id) || chosen;
  }

  /* One add at a time per button: a second press while the request is in flight
   * would put a second identical line in the bag. */
  function addToCart(entry) {
    if (!entry || !entry.variantId) return;
    var row = rowFor(entry.id);
    var addBtn = row ? qs('[data-runit-wishlist-add]', row) : null;
    if (addBtn && addBtn.__rlBusy) return;
    var fromCard = !!row && bagV2() && row.classList.contains('rl-wishlist-item--card');
    if (addBtn) {
      addBtn.__rlBusy = true;
      addBtn.classList.add('is-loading');
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
    }
    if (row) {
      clearRowError(row);
      setRowStatus(row, 'Adding to your bag…');
    }

    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      /* sections/sections_url is the theme's own contract (see product-form in
       * assets/main.js): one request adds the item AND returns fresh cart drawer
       * + icon bubble markup, so nothing has to be polled or fetched twice. */
      body: JSON.stringify({
        items: [entry.properties && Object.keys(entry.properties).length
          ? { id: entry.variantId, quantity: 1, properties: entry.properties }
          : { id: entry.variantId, quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname + window.location.search
      })
    })
      .then(function (res) {
        return res.json().catch(function () { return null; }).then(function (data) {
          if (!res.ok) throw new Error('HTTP ' + res.status + (data && data.description ? ': ' + data.description : ''));
          return data;
        });
      })
      .then(function (data) {
        withSuppress(function () {
          if (data && data.sections) applySections(data.sections);
        });
        cartVariants[String(entry.variantId)] = true;
        var fresh = rowFor(entry.id);
        if (fresh) {
          var button = qs('[data-runit-wishlist-add]', fresh);
          if (button) {
            button.__rlBusy = false;
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
          }
          setRowStatus(fresh, 'Added to your bag.');
        }
        paintRows();
        /* The bag is now the authority again — confirm with the server. */
        syncCartState(300);
        /* Bag v2 card: the tick shows, then the drawer turns to the bag. The item
         * stays saved — adding never unsaves. */
        if (fromCard) {
          window.setTimeout(function () {
            if (bagV2() && isWishlistView()) showBag();
          }, 300);
        }
      })
      .catch(function (error) {
        var current = rowFor(entry.id);
        if (current) {
          var button = qs('[data-runit-wishlist-add]', current);
          if (button) {
            button.__rlBusy = false;
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
          }
        }
        /* Never leave a button claiming IN CART after a failed add. */
        paintRows();
        setRowError(rowFor(entry.id), messageFor(error));
      });
  }

  /* --------------------------------------------------------------- the drawer */

  function setHeading(wishlist) {
    var el = drawer();
    if (!el) return;
    var heading = qs('[data-runit-wishlist-heading]', el);
    if (!heading) return;
    if (!heading.__rlDefaultHeading) heading.__rlDefaultHeading = heading.textContent;
    heading.textContent = wishlist ? wishlistLabel : heading.__rlDefaultHeading;
  }

  function showWishlist() {
    var el = drawer();
    if (!el) return;
    var open = el.classList.contains('active');
    el.classList.add('rl-wishlist-open');
    setHeading(true);
    render();
    refreshFromStorefront();
    /* The bag may have changed on another tab, or before this page load. */
    syncCartState(0);
    if (open) return;
    /* Opening goes through the theme's own cart icon so the overlay, focus and
     * body lock all behave exactly as they do for the bag. */
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) bubble.click();
    el.classList.add('rl-wishlist-open');
    window.setTimeout(function () {
      el.classList.add('rl-wishlist-open');
      render();
    }, 0);
  }

  function showBag() {
    var el = drawer();
    if (!el) return;
    el.classList.remove('rl-wishlist-open');
    setHeading(false);
  }

  /* The saved variant's option values, when the markup knows them (the cart
   * line does — that IS the variant the shopper bought). Parsed defensively:
   * a broken attribute must not stop the heart from saving anything at all. */
  function variantOptionsOf(host) {
    if (!host) return [];
    if (host.getAttribute('data-product-variant-chosen') !== 'true') return [];
    var raw = host.getAttribute('data-product-variant-options');
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* The non-size options the shopper is looking at, by option position, size left
   * blank — so bag v2's wishlist card only has to ask for a size. The bag line
   * knows its variant's values (and, with data-product-option-names, which one is
   * the size); the product page's heart reads the live picker beside it. A heart
   * anywhere else knows nothing and saves none. */
  function contextOf(host) {
    var names = [];
    try { names = JSON.parse(host.getAttribute('data-product-option-names') || '[]') || []; } catch (e) { names = []; }
    var values = variantOptionsOf(host);
    if (values.length && Array.isArray(names) && names.length) {
      return values.map(function (v, i) { return /size/i.test(String(names[i] || '')) ? '' : String(v || ''); });
    }
    var section = host.closest('.shopify-section') || document;
    var handle = host.getAttribute('data-product-handle') || '';
    var picker = qsa('variant-selects', section).filter(function (el) {
      var m = (el.getAttribute('data-url') || '').match(/\/products\/([^/?#]+)/);
      return !!m && m[1] === handle;
    })[0];
    if (!picker) return [];
    return qsa(':scope > .product-form__input', picker).map(function (field) {
      if (field.hasAttribute('data-runit-size-option')) return '';
      var checked = qs('input[type="radio"]:checked', field);
      var select = qs('select', field);
      return checked ? checked.value : select ? select.value : '';
    });
  }

  /* A personalised product page opts in by marking its main choice with data-wishlist-note (the
   * keychain's design property). Then the heart saves what is on screen, not the page-load default:
   * the selected variant, its option values, every visible line-item property and that note. */
  function liveChoice(host) {
    var section = host.closest('.shopify-section');
    var noteEl = section && qs('[data-wishlist-note]', section);
    var form = noteEl && document.getElementById(noteEl.getAttribute('form') || '');
    var idEl = form && qs('[name="id"]', form);
    if (!idEl || !idEl.value) return null;
    var props = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      var m = /^properties\[(.+)\]$/.exec(el.name || '');
      if (m && m[1].charAt(0) !== '_' && el.value) props[m[1]] = el.value;
    });
    var values = contextOf(host).filter(Boolean);
    return { variantId: idEl.value, variantTitle: values.join(' / '), options: values, properties: props, note: noteEl.value };
  }

  function entryFrom(host) {
    if (!host) return null;
    var live = liveChoice(host);
    if (live) {
      return {
        id: host.getAttribute('data-product-id'),
        handle: host.getAttribute('data-product-handle'),
        title: host.getAttribute('data-product-title'),
        price: host.getAttribute('data-product-price'),
        compare: host.getAttribute('data-product-compare'),
        image: host.getAttribute('data-product-image'),
        variantId: live.variantId,
        variantChosen: true,
        variantTitle: live.variantTitle,
        options: live.options,
        context: live.options,
        properties: live.properties,
        note: live.note,
        available: host.getAttribute('data-product-available') !== 'false',
        hasRange: Number(host.getAttribute('data-product-price-max')) > Number(host.getAttribute('data-product-price')),
        addedAt: Date.now()
      };
    }
    return {
      id: host.getAttribute('data-product-id'),
      handle: host.getAttribute('data-product-handle'),
      title: host.getAttribute('data-product-title'),
      price: host.getAttribute('data-product-price'),
      compare: host.getAttribute('data-product-compare'),
      image: host.getAttribute('data-product-image'),
      variantId: host.getAttribute('data-product-variant-id'),
      /* Only the cart line (and a variant the shopper picked here) knows a
       * variant was actually chosen; a product card hands over the default
       * variant, so its option values are deliberately not carried over. */
      variantChosen: host.getAttribute('data-product-variant-chosen') === 'true',
      variantTitle: host.getAttribute('data-product-variant-title') || '',
      options: variantOptionsOf(host),
      context: contextOf(host),
      available: host.getAttribute('data-product-available') !== 'false',
      hasRange: Number(host.getAttribute('data-product-price-max')) > Number(host.getAttribute('data-product-price')),
      addedAt: Date.now()
    };
  }

  /* ------------------------------------------------------------------ events */

  /* A dropdown reports a pick as `change`, not as a click, so the option rows
   * need their own delegated listener. It resolves the same way a size chip
   * always did: record the value, then let paintRow decide what the button says. */
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || !select.matches || !select.matches('[data-runit-wishlist-option-select]')) return;
    if (!String(select.value || '')) return;
    var row = select.closest('[data-runit-wishlist-item]');
    if (!row) return;
    var entry = store.get(row.getAttribute('data-runit-wishlist-item'));
    if (!entry) return;
    chooseOption(row, entry, Number(select.getAttribute('data-runit-wishlist-option-select')), select.value);
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var toggle = target.closest('[data-runit-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      var host = toggle.closest('[data-runit-wishlist-product]');
      var entry = entryFrom(host);
      if (!entry) return;
      var result = store.toggle(entry);
      if (result === 'added') {
        syncToAccount('add', entry);
        if (bagV2()) {
          showToast('Item added to your wishlist', 'View', showWishlist);
        } else if (!maybePrompt(entry.title)) {
          showToast('Saved to your wishlist.', 'View wishlist', showWishlist);
        }
      } else {
        syncToAccount('remove', entry);
        showToast(bagV2() ? 'Item removed from your wishlist' : 'Removed from your wishlist.', 'Undo', function () {
          store.add(entry);
          syncToAccount('add', entry);
        });
      }
      return;
    }

    /* Bag v2 card menu: ⋯ opens it, anything else closes it. */
    var moreToggle = target.closest('[data-runit-wishlist-more-toggle]');
    closeMenus(moreToggle ? moreToggle.parentNode : null);
    if (moreToggle) {
      event.preventDefault();
      var menu = qs('[data-runit-wishlist-menu]', moreToggle.parentNode);
      if (menu) {
        menu.hidden = !menu.hidden;
        moreToggle.setAttribute('aria-expanded', String(!menu.hidden));
        var firstItem = !menu.hidden && qs('[role="menuitem"]:not([hidden])', menu);
        if (firstItem) firstItem.focus({ preventScroll: true });
      }
      return;
    }
    var menuAdd = target.closest('[data-runit-wishlist-menu-add]');
    if (menuAdd) {
      event.preventDefault();
      var menuAddRow = menuAdd.closest('[data-runit-wishlist-item]');
      var rowAdd = menuAddRow && qs('[data-runit-wishlist-add]', menuAddRow);
      if (rowAdd && !rowAdd.hidden) rowAdd.click();
      return;
    }
    var menuNotify = target.closest('[data-runit-wishlist-menu-notify]');
    if (menuNotify) {
      event.preventDefault();
      var notifyRow = menuNotify.closest('[data-runit-wishlist-item]');
      var notifyEntry = notifyRow && store.get(notifyRow.getAttribute('data-runit-wishlist-item'));
      if (notifyEntry) openNotify(notifyRow, notifyEntry);
      return;
    }

    var addBtn = target.closest('[data-runit-wishlist-add]');
    if (addBtn) {
      event.preventDefault();
      if (addBtn.disabled) return;
      var addRow = addBtn.closest('[data-runit-wishlist-item]');
      var addEntry = addRow ? store.get(addRow.getAttribute('data-runit-wishlist-item')) : null;
      if (!addEntry) return;
      var state = rowState(addEntry);
      /* Already in the bag? Take them there. */
      if (state.inCart) {
        viewBag();
        return;
      }
      if (state.gone) return;
      /* Nothing chosen yet: put the cursor in the dropdown that is outstanding
       * rather than guessing a size on the shopper's behalf. */
      if (state.needsChoice) {
        if (state.card && state.options.missing.length) {
          setRowError(addRow, 'Please select a ' + String(state.options.missing[0].name || 'size').toLowerCase());
          return;
        }
        revealOptions(addRow, addEntry);
        return;
      }
      if (!state.available) {
        if (notifyOn && notifyVariantId(state, addEntry) && !isNotified(notifyVariantId(state, addEntry))) {
          openNotify(addRow, addEntry);
        }
        return;
      }
      /* A card whose saved colour/print completes the variant (no size to ask
       * for): record that variant as the pick, so the bag gets exactly it. */
      if (state.card && addEntry.variantChosen !== true && state.options.variant) {
        addEntry = commitSelection(addEntry, state.product, state.options.selection);
      }
      addToCart(addEntry);
      return;
    }

    var changeBtn = target.closest('[data-runit-wishlist-change]');
    if (changeBtn && !changeBtn.hidden) {
      event.preventDefault();
      var changeRow = changeBtn.closest('[data-runit-wishlist-item]');
      var changeEntry = changeRow ? store.get(changeRow.getAttribute('data-runit-wishlist-item')) : null;
      if (changeEntry) revealOptions(changeRow, changeEntry);
      return;
    }

    var removeBtn = target.closest('[data-runit-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      var removed = store.get(removeBtn.getAttribute('data-runit-wishlist-remove'));
      if (!removed) return;
      store.remove(removed.id);
      syncToAccount('remove', removed);
      showToast(bagV2() ? 'Item removed from your wishlist' : 'Removed from your wishlist.', 'Undo', function () {
        store.add(removed);
        syncToAccount('add', removed);
      });
      return;
    }

    if (target.closest('[data-runit-wishlist-tab]')) {
      event.preventDefault();
      showWishlist();
      return;
    }

    /* The bag tab is a real link to /cart, so it keeps that behaviour — but
     * while the wishlist is showing it should come back to the bag first. */
    var bagTab = target.closest('[data-runit-bag-tab]');
    if (bagTab && isWishlistView()) {
      event.preventDefault();
      showBag();
      return;
    }

    var toastAction = target.closest('[data-runit-wishlist-toast-action]');
    if (toastAction) {
      event.preventDefault();
      var action = toastAction.__rlAction;
      hideToast();
      if (typeof action === 'function') action();
      return;
    }

    if (target.closest('[data-runit-wishlist-toast-close]') || target.closest('[data-runit-wishlist-sheet-dismiss]')) {
      hideToast();
      hideSheet();
      return;
    }

    /* Drop the wishlist view when the drawer closes, so the next open is the bag. */
    if (target.closest('.drawer__close') || target.closest('#CartDrawer-Overlay') || target.closest('.cart-drawer__overlay')) {
      showBag();
    }
  });

  function closeMenus(except) {
    qsa('[data-runit-wishlist-more]').forEach(function (more) {
      if (more === except) return;
      var menu = qs('[data-runit-wishlist-menu]', more);
      if (menu && !menu.hidden) {
        menu.hidden = true;
        var toggle = qs('[data-runit-wishlist-more-toggle]', more);
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeMenus(null);
      hideSheet();
      hideToast();
    }
  });

  store.subscribe(render);

  /* --------------------------------------------------- account boot sequence
   *
   * Signed in, in this order:
   *   1. paint from this device's cache of the account list (instant, offline);
   *   2. fold in what Liquid rendered, if it rendered anything;
   *   3. ask the endpoint, which is the authority, and fold that in too.
   * A guest never gets here at all and behaves exactly as before. */
  function adoptAccountList() {
    if (!account || account.mode !== 'account') return;
    var known = account.accountKnown() ? account.state.account : accountItems;
    var plan = account.adopt(store.all(), known);
    if (plan.items.length !== store.all().length || plan.merge) store.replace(plan.items);
    paintSyncState();
  }

  function bootAccount() {
    if (!account || account.mode !== 'account') { paintSyncState(); return; }
    /* Coming back to a page restored from the bfcache can mean the shopper signed
     * out or in somewhere else, so the identity is re-checked rather than assumed. */
    var shown = config.getAttribute('data-customer-id') || '';
    if (shown !== customerId) { account.reset(); return; }
    adoptAccountList();
    account.load(true).then(function (result) {
      if (result && result.items) {
        account.adopt(store.all(), result.items);
        var rows = core.cacheFromAccountItems(result.items, store.all());
        store.replace(rows);
      } else {
        /* Still queued work from a previous visit? Send it now that we are here. */
        account.flush();
      }
      paintSyncState();
    });
  }

  /* The one line that tells a signed-in shopper where their list lives. It only
   * claims sync when the server has actually answered — "synced" next to a dead
   * endpoint would be a lie, and the queue is visible while it is waiting. */
  function paintSyncState() {
    var host = qs('[data-runit-wishlist-sync]');
    if (!host) return;
    if (!account || account.mode !== 'account') { host.hidden = true; return; }
    var state = account.state;
    var text = '';
    /* `off` is "there is no account backend at all", not "we cannot reach one":
     * the wishlist is device-local on this store, so a signed-in shopper is in
     * exactly the same position as a guest and there is nothing to say about a
     * sync that is not going to happen. Nothing else here can be allowed to speak
     * for this state, which is why it is checked first — a queue waiting for a
     * server that does not exist is not "syncing when it can". */
    if (state.server === 'off') text = '';
    else if (state.syncing) text = 'Syncing your wishlist…';
    else if (state.pending > 0) text = 'Saved on this device. Syncing when it can — nothing is lost.';
    else if (state.server === 'up') text = 'Synced to your account';
    else text = '';
    host.textContent = text;
    host.hidden = !text;
  }

  if (account) account.subscribe(paintSyncState);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bootAccount();
      scheduleSync();
    });
  } else {
    bootAccount();
    scheduleSync();
  }

  /* Safari restores pages from the bfcache without re-running any script, so the
   * list is re-checked when a restored page becomes visible again. */
  window.addEventListener('pageshow', function (event) {
    if (account && event && event.persisted) bootAccount();
  });

  /* The bag changed: anything the theme does to the drawer or the icon bubble
   * (its own add-to-cart, a quantity change, a remove) is a re-render of one of
   * those two nodes, so watch both and re-read the cart instead of guessing. */
  function cartChanged() {
    if (suppress) return;
    syncCartState();
  }

  if (window.MutationObserver) {
    /* 1 · Collection grids re-render in place (filters, "show more"), so the
     *     hearts drawn into them need their saved state applied again. */
    new MutationObserver(function () {
      if (!suppress) scheduleSync();
    }).observe(document.body, { childList: true, subtree: true });

    /* 2 · The theme swaps #CartDrawer's HTML after every cart change, which
     *     replaces our panel with the server-rendered copy — redraw it, and
     *     put the drawer back on the wishlist view if it was there. */
    var cartInner = document.getElementById('CartDrawer');
    if (cartInner) {
      new MutationObserver(function () {
        if (suppress) return;
        render();
        cartChanged();
      }).observe(cartInner, { childList: true });
    }

    /* 3 · The icon bubble is re-rendered with the same response, including when
     *     the shopper removes a line from the bag itself. */
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      new MutationObserver(cartChanged).observe(bubble, { childList: true, subtree: true });
    }
  }
})();
